// =============================================================================
// PPI — sub-hub do Programa de Promoção Interna (embutido no módulo RH)
// =============================================================================
// Renderizado DENTRO do contentBox do RH.js (que já traz Sidebar + header do
// módulo). Aqui só a navegação interna entre as telas do PPI.

import React, { useState } from 'react';
import { ClipboardList, ListChecks, FileQuestion, Trophy, Library, Footprints, History } from 'lucide-react';
import { CORES } from './ppiUI';
import PPIProcessos from './PPIProcessos';
import PPITriagem from './PPITriagem';
import PPIBanco from './PPIBanco';
import PPIProvas from './PPIProvas';
import PPIAplicacao from './PPIAplicacao';
import PPIChao from './PPIChao';
import PPIChaoVersoes from './PPIChaoVersoes';
import PPIHistorico from './PPIHistorico';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// A ordem das abas segue o CAMINHO REAL do processo: abrir o processo → triar os
// inscritos → (banco de questões) → montar a prova → aplicar e ranquear → avaliar
// na LIVE 30 dias depois → e o histórico, que olha tudo isso de cima.
const ABAS = [
    { id: 'processos', label: 'Processos & Vagas', icon: ClipboardList },
    { id: 'triagem', label: 'Triagem', icon: ListChecks },
    { id: 'banco', label: 'Banco de questões', icon: Library },
    { id: 'provas', label: 'Provas', icon: FileQuestion },
    { id: 'aplicacao', label: 'Aplicação & Ranking', icon: Trophy },
    { id: 'chao', label: 'LIVE', icon: Footprints },
    { id: 'historico', label: 'Histórico', icon: History },
];

// Quem vê cada aba. O RH toca a ponta de RH (processos + triagem) e o Histórico;
// o Treinamento vê TODAS as abas desde 2026-08-11 (antes ficava só na ponta
// técnica — banco, provas, aplicação, LIVE — e não conseguia abrir o processo nem
// triar os inscritos, que é trabalho que ele também faz). Admin e Diretoria não
// entram no mapa (são tratados à parte). O corte é só de TELA: os endpoints são
// compartilhados entre abas (ver a nota de PERFIS_OPERA no backend).
const ACESSO_ABA = {
    processos: ['RH', 'Treinamento', 'Operacional'],
    triagem: ['RH', 'Treinamento', 'Operacional'],
    banco: ['Treinamento', 'Operacional'],
    provas: ['Treinamento', 'Operacional'],
    aplicacao: ['Treinamento', 'Operacional'],
    chao: ['Treinamento', 'Operacional'],
    historico: ['RH', 'Treinamento', 'Operacional'],
};

// Perfil do usuário logado (mesmo padrão do RH.js: objeto 'user' no localStorage).
function perfilDoUsuario() {
    try {
        return perfilNoModulo((JSON.parse(localStorage.getItem('user') || '{}')).perfil, 'treinamento') || '';
    } catch (_) {
        return '';
    }
}

function PPI() {
    const perfil = perfilDoUsuario();
    // Admin e Diretoria veem todas as abas; os demais, só as do seu recorte.
    const vezTudo = perfil === 'Admin' || perfil === 'Diretoria';
    const abasVisiveis = ABAS.filter((a) => vezTudo || (ACESSO_ABA[a.id] || []).includes(perfil));

    // A aba inicial é a 1ª visível do perfil — hoje todos os perfis com acesso abrem
    // em Processos (é o começo do caminho real). A regra continua sendo "a 1ª visível"
    // para que um perfil de recorte parcial nunca caia numa aba que ele não vê.
    const [aba, setAba] = useState(abasVisiveis[0]?.id || 'processos');

    return (
        <div>
            <div style={{ marginBottom: '8px' }}>
                <h2 style={{ fontSize: '22px', fontWeight: 800, color: CORES.texto, margin: 0 }}>
                    PPI — Programa de Promoção Interna
                </h2>
                <p style={{ fontSize: '14px', color: CORES.textoFraco, margin: '4px 0 0 0' }}>
                    Inscrição por link, triagem, prova com ranking e LIVE — o caminho inteiro da promoção interna.
                </p>
            </div>

            {/* Abas internas (só as que o perfil pode ver) */}
            <div style={{ display: 'flex', gap: '8px', margin: '20px 0 24px 0', flexWrap: 'wrap' }}>
                {abasVisiveis.map((a) => (
                    <button
                        key={a.id}
                        onClick={() => setAba(a.id)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 18px', borderRadius: '8px', fontWeight: 700, fontSize: '14px',
                            cursor: 'pointer', transition: 'all 0.2s',
                            border: aba === a.id ? `2px solid ${CORES.primaria}` : '2px solid transparent',
                            backgroundColor: aba === a.id ? CORES.primariaBg : '#f8fafc',
                            color: aba === a.id ? CORES.primaria : CORES.textoFraco,
                        }}
                    >
                        <a.icon size={18} /> {a.label}
                    </button>
                ))}
            </div>

            {aba === 'processos' && <PPIProcessos />}
            {aba === 'triagem' && <PPITriagem />}
            {aba === 'banco' && <PPIBanco />}
            {aba === 'provas' && <PPIProvas />}
            {aba === 'aplicacao' && <PPIAplicacao />}
            {aba === 'chao' && <AvaliacaoChao />}
            {aba === 'historico' && <PPIHistorico />}
        </div>
    );
}

// A avaliação de chão tem duas faces: a WORKLIST (quem avaliar) e o CONSTRUTOR do
// formulário (que o Treinamento monta, porque as perguntas ainda não existem).
// Como sub-aba para não estourar a barra de abas do topo.
function AvaliacaoChao() {
    const [sub, setSub] = useState('candidatos');
    const SUBS = [
        { id: 'candidatos', label: 'Candidatos a avaliar' },
        { id: 'formularios', label: 'Formulários' },
    ];
    return (
        <div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
                {SUBS.map((s) => (
                    <button key={s.id} onClick={() => setSub(s.id)}
                        style={{
                            padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                            border: `1px solid ${sub === s.id ? CORES.primaria : CORES.borda}`,
                            backgroundColor: sub === s.id ? CORES.primaria : '#fff',
                            color: sub === s.id ? '#fff' : CORES.textoSec,
                        }}>
                        {s.label}
                    </button>
                ))}
            </div>
            {sub === 'candidatos' ? <PPIChao /> : <PPIChaoVersoes />}
        </div>
    );
}

export default PPI;
